import { LineItem, Move, Mover } from "@/types";

const RateCharges = ({ move, mover }: { move: Move; mover?: Mover }) => {
  return (
    <div className="flex flex-col gap-4">
      {/* make a table with columns: name, quantity, price, total */}
      {/* if billing type is hourly, first row should be name: hourly rate, quantity: hours estimate, price: hourly rate, total: hours estimate * hourly rate */}
      {/* if billing type is flat rate, first row should be name: flat rate, quantity: 1, price: flat rate, total: flat rate */}
      {/* then show all the billing line items */}
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th scope="col" className="text-left text-sm">
              Item
            </th>
            <th scope="col" className="text-left text-sm">
              Rate
            </th>
            <th scope="col" className="text-left text-sm">
              Qty
            </th>
            <th scope="col" className="text-left text-sm text-right">
              Total
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {move.line_items?.map((item: LineItem, index: number) => (
            <tr key={index}>
              <td className="whitespace-nowrap py-1 pl-0 pr-3 sm:pl-0 text-sm">
                {item.name}
                {item.description && (
                  <span className="text-stone-800 text-sm font-normal">
                    {" "}
                    ({item.description})
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap py-1 text-sm space-x-1 flex flex-col">
                {item.discount > 0 && (
                  <span className="line-through text-red-500">
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(parseFloat(item.price?.toString() || "0"))}
                  </span>
                )}
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(
                  parseFloat(item.price?.toString() || "0") -
                    parseFloat(item.discount?.toString() || "0")
                )}
                {item.rate_qualifier}
              </td>
              <td className="whitespace-nowrap py-1 text-sm">
                {item.qty
                  ? parseFloat(
                      parseFloat(item.qty?.toString() || "0").toFixed(2)
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })
                  : "TBD"}
              </td>
              <td className="whitespace-nowrap py-1 text-sm text-right">
                {!item.qty
                  ? "-"
                  : Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(parseFloat(item.subtotal?.toString() || "0"))}
              </td>
            </tr>
          ))}
          {(mover?.business_type !== "mover" ||
            mover?.mover_configuration.estimates_show_time_and_total) &&
            !move.invoice?.id && (
              <tr>
                <td colSpan={3} className="text-right text-sm font-semibold">
                  Estimated Total
                </td>
                <td className="whitespace-nowrap py-1 text-sm text-right font-bold">
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(
                    move.line_items?.reduce(
                      (acc, item) => acc + Number(item.subtotal),
                      0
                    ) || 0
                  )}
                </td>
              </tr>
            )}
          {move.not_to_exceed_price && (
            <tr>
              <td
                colSpan={3}
                className="text-right text-sm font-semibold whitespace-nowrap"
              >
                Not to Exceed Price
              </td>
              <td className="whitespace-nowrap py-1 text-sm text-right font-bold">
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(move.not_to_exceed_price)}
              </td>
            </tr>
          )}
          {move.invoice?.id && (
            <tr>
              <td
                colSpan={3}
                className="whitespace-nowrap py-1 pl-4 pr-3 sm:pl-0 text-sm text-right font-bold"
              >
                Subtotal
              </td>
              <td className="whitespace-nowrap py-1 text-sm text-right font-bold">
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(move.invoice?.line_items_total || 0)}
              </td>
            </tr>
          )}
          {move.invoice?.id && (
            <tr>
              <td
                className="whitespace-nowrap py-1 pl-4 pr-3 sm:pl-0 text-sm text-right"
                colSpan={3}
              >
                Final Discount
              </td>
              <td className="whitespace-nowrap py-1 text-sm text-right">
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(move.invoice?.discount || 0)}
              </td>
            </tr>
          )}
          {move.invoice?.id && (
            <tr>
              <td
                className="whitespace-nowrap py-1 pl-4 pr-3 sm:pl-0 text-sm text-right"
                colSpan={3}
              >
                Crew Tip
              </td>
              <td className="whitespace-nowrap py-1 text-sm text-right">
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(move.invoice?.tip || 0)}
              </td>
            </tr>
          )}
          {move.invoice?.total && (
            <tr>
              <td
                className="whitespace-nowrap py-1 pl-4 pr-3 sm:pl-0 text-base font-bold text-sm text-right"
                colSpan={3}
              >
                Final Total
              </td>
              <td className="whitespace-nowrap py-1 text-sm text-right font-bold">
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(move.invoice?.total || 0)}
              </td>
            </tr>
          )}
          {move.invoice?.id && move.invoice?.processing_fee_amount && (
            <tr>
              <td
                colSpan={3}
                className="whitespace-nowrap py-1 pl-4 pr-3 sm:pl-0 text-sm text-right"
              >
                Processing Fees
              </td>
              <td className="whitespace-nowrap py-1 text-sm text-right">
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(
                  move.charges.reduce(
                    (acc, charge) => acc + Number(charge.processing_fee_amount),
                    0
                  )
                )}
              </td>
            </tr>
          )}
          {move.invoice?.id && (
            <tr>
              <td
                colSpan={3}
                className="whitespace-nowrap py-1 pl-4 pr-3 sm:pl-0 text-sm text-right"
              >
                Payments
              </td>
              <td className="whitespace-nowrap py-1 text-sm text-right">
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(
                  Number(
                    move.charges.reduce(
                      (acc, charge) => acc + Number(charge.amount),
                      0
                    )
                  )
                )}
              </td>
            </tr>
          )}
          {move.invoice?.id && (
            <tr>
              <td
                className="whitespace-nowrap py-1 pl-4 pr-3 sm:pl-0 text-base text-sm font-bold text-right"
                colSpan={3}
              >
                Balance Due
              </td>
              <td className="whitespace-nowrap py-1 text-sm text-right font-bold">
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(move.invoice?.due_amount || 0)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default RateCharges;
